import { checkExistingEmails } from '../../../graphql/services/user.service';

/**
 * Pragmatic client-side shape check. Deliberately looser than the backend's
 * `isValidUserEmail` — this exists to catch typos before a round trip, not to
 * be the authority on what the platform accepts.
 */
export const validEmailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The single normalisation both paths apply before validating, comparing or
 * sending an address. `validEmailRe` and the API's `isValidUserEmail` are both
 * anchored and allow no surrounding whitespace, so a pasted address has to be
 * trimmed *before* it is tested or it fails as malformed rather than being
 * cleaned up. Lowercasing matches what the API stores in `email` / `email_lower`.
 */
export const normaliseUserEmail = (email: string): string => email.trim().toLowerCase();

/** Copy shared by the create and edit paths so both report a clash identically. */
export const EMAIL_TAKEN_MESSAGE = 'That email address already belongs to another user.';
export const EMAIL_INVALID_MESSAGE = 'Enter a valid email address.';

/**
 * Checks an address against existing users — case-insensitively and including
 * soft-deleted records, since a deleted user's address cannot be reused either.
 *
 * A lookup failure resolves to `false` (available) on purpose: this is only the
 * pre-submit UX guard, and `createUser` / `updateUser` remain the authoritative
 * duplicate check. Failing closed here would block saves whenever the query is
 * briefly unavailable.
 *
 * @param email The address to test.
 * @returns Whether the address is already taken.
 */
export const isEmailTaken = async (email: string): Promise<boolean> => {
  const normalised: string = normaliseUserEmail(email);
  if (!validEmailRe.test(normalised)) return false;
  try {
    const existing: Set<string> = await checkExistingEmails([normalised]);
    return existing.has(normalised);
  } catch {
    return false;
  }
};
